import React, { useState, useEffect } from "react";
import { SubPageHeader } from "../../components/ui/SubPageHeader";
import { Sun, Printer } from "lucide-react";
import { Button } from "../../components/ui/Button";
import { ReportActionToolbar } from "../../components/reports/ReportActionToolbar";
import { PaginationFooter } from "../../components/sunbeds/PaginationFooter";
import {
  SunbedReportTable,
  SunbedReportItem,
} from "../../components/reports/SunbedReportTable";
import { fetchSunbedReport } from "../../services/reportsApi";

const mapApiDataToReportItem = (
  apiData: { customer_name: string; sunbed_package: string; sunbed_mins: string; amount: string; gateway: string; recharged_date: string }[],
  startId = 1
): SunbedReportItem[] =>
  apiData.map((row, idx) => ({
    id: String(startId + idx),
    customer: row.customer_name,
    package: row.sunbed_package,
    mins: row.sunbed_mins,
    amount: row.amount,
    gateway: row.gateway,
    rechargedDate: row.recharged_date,
  }));

const SunbedReport: React.FC = () => {
  const [reports, setReports] = useState<SunbedReportItem[]>([]);
  const [searchTerm, setSearchTerm] = useState("");
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage] = useState(10);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;
    setIsLoading(true);
    fetchSunbedReport()
      .then((res) => {
        if (!cancelled) {
          setReports(mapApiDataToReportItem(res.data));
        }
      })
      .catch(() => {
        if (!cancelled) {
          setReports([]);
        }
      })
      .finally(() => {
        if (!cancelled) {
          setIsLoading(false);
        }
      });
    return () => {
      cancelled = true;
    };
  }, []);

  const filteredReports = reports.filter((r) =>
    r.customer.toLowerCase().includes(searchTerm.toLowerCase()) ||
    r.package.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const indexOfLastItem = currentPage * itemsPerPage;
  const indexOfFirstItem = indexOfLastItem - itemsPerPage;
  const currentReports = filteredReports.slice(
    indexOfFirstItem,
    indexOfLastItem
  );

  const handlePageChange = (page: number) => {
    setCurrentPage(page);
  };

  return (
    <div className="flex-1 flex flex-col space-y-6 overflow-hidden p-4">
      <SubPageHeader
        title="Sunbed Report"
        subtitle="View sunbed usage and revenue reports"
        icon={Sun}
        actions={
          <div className="flex items-center gap-3">
            <Button
              className="bg-[#885ABB] hover:bg-[#7748A4] text-white flex items-center gap-2 px-3 py-2 shadow-sm"
              title="Print Report"
            >
              <Printer size={18} />
            </Button>
          </div>
        }
      />

      <ReportActionToolbar
        totalItems={reports.length}
        showingStart={filteredReports.length > 0 ? indexOfFirstItem + 1 : 0}
        showingEnd={Math.min(indexOfLastItem, filteredReports.length)}
        searchTerm={searchTerm}
        onSearchChange={(val) => {
          setSearchTerm(val);
          setCurrentPage(1);
        }}
        searchPlaceholder="Search reports..."
      />

      <div className="flex-1 overflow-hidden flex flex-col">
        <SunbedReportTable
          reports={currentReports}
          isLoading={isLoading}
          indexOfFirstItem={indexOfFirstItem}
        />
        <PaginationFooter
          totalItems={reports.length}
          filteredTotal={filteredReports.length}
          currentPage={currentPage}
          itemsPerPage={itemsPerPage}
          onPageChange={handlePageChange}
        />
      </div>
    </div>
  );
};

export default SunbedReport;
